
import React from 'react';
import { Project } from '../types';

interface ProjectDetailProps {
  project: Project;
  onClose: () => void;
}

const ProjectDetail: React.FC<ProjectDetailProps> = ({ project, onClose }) => {
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex justify-end animate-in fade-in duration-300">
      <div className="w-full max-w-4xl bg-zinc-950 h-full overflow-y-auto border-l border-zinc-800 animate-in slide-in-from-right duration-500">
        
        <div className="sticky top-0 z-10 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800 px-8 h-16 flex items-center justify-between">
          <button onClick={onClose} className="font-mono text-[10px] uppercase text-zinc-500 hover:text-white transition-colors flex items-center gap-2 cursor-pointer bg-transparent border-none p-0">
            <i className="fa-solid fa-arrow-left"></i> Close_Registry
          </button>
          <div className="flex items-center gap-6">
            {project.github && (
              <a 
                href={project.github} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-zinc-500 hover:text-white transition-colors no-underline text-xl"
                title="View Source"
              >
                <i className="fa-brands fa-github"></i>
              </a>
            )}
            <a 
              href={project.link} 
              target="_blank" 
              className="px-4 py-2 bg-red-600 text-white font-mono text-[10px] uppercase font-bold tracking-widest no-underline hover:bg-red-700 transition-colors shadow-lg"
            >
              Launch_App
            </a>
          </div>
        </div>

        <div className="p-12">
          <div className="font-mono text-xs text-red-600 uppercase tracking-[0.4em] mb-4 flex items-center gap-2">
            <i className="fa-brands fa-hive"></i>
            Module_Deployment_Details
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter uppercase mb-8 leading-none">{project.title}</h1>
          
          <div className="aspect-video w-full border border-zinc-800 bg-zinc-900 mb-8 overflow-hidden group relative">
            <img src={project.image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>

          <div className="mb-12 flex justify-start">
            <a 
              href={project.link} 
              target="_blank" 
              className="inline-flex items-center gap-4 px-8 py-5 bg-white text-black font-mono text-xs font-bold uppercase tracking-[0.2em] hover:bg-red-600 hover:text-white transition-all no-underline active:scale-95"
            >
              Initialize_Live_Environment
              <i className="fa-solid fa-chevron-right text-[10px]"></i>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-8">
              <h3 className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-6 pb-2 border-b border-zinc-800 inline-block">Abstract</h3>
              <p className="text-2xl text-zinc-300 font-light leading-snug italic mb-8">
                {project.longDescription}
              </p>
              <div className="h-px w-20 bg-red-600 mb-8"></div>
              <p className="text-zinc-500 font-light leading-relaxed">
                {project.description}
              </p>
            </div>

            <div className="md:col-span-4 space-y-8">
              <div>
                <h4 className="font-mono text-[10px] uppercase text-zinc-600 mb-4 tracking-widest">Stack_Manifest</h4>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map(t => (
                    <span key={t} className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 font-mono text-[10px] uppercase text-zinc-400">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              
              {project.announcementUrl && (
                <a href={project.announcementUrl} target="_blank" className="block p-6 border border-zinc-800 hover:bg-zinc-900 no-underline group transition-all bg-zinc-950">
                  <div className="font-mono text-[10px] text-zinc-600 uppercase mb-2 tracking-widest flex items-center gap-2">
                    <i className="fa-brands fa-hive text-red-600"></i>
                    Social_Broadcast
                  </div>
                  <div className="text-white text-sm font-bold group-hover:text-red-600 flex items-center justify-between">
                    View_Announcement
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                  </div>
                </a>
              )}

              <div className="p-6 border border-zinc-800 bg-zinc-900/20">
                <div className="font-mono text-[10px] text-zinc-600 uppercase mb-4 tracking-widest">Registry_ID</div>
                <div className="text-zinc-500 font-mono text-[11px] break-all">
                  HIVE::{project.id.toUpperCase().replace(/-/g, '_')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
